import type { ChartData } from 'chart.js';
import { useMemo } from 'react';

import useChartColor from '@/features/(charts)/chart-renderer/hooks/use-chart-color';
import useChartLabel from '@/features/(charts)/chart-renderer/hooks/use-chart-label';
import chartUtils from '@/features/(charts)/chart-renderer/lib/chart-utils';
import useColor from '@/hooks/domain/useColor';
import { getAlphaColor } from '@/styles/colors/color.utils';

type Props = {
  chart: ChartBase;
  tableData: ChartTableRes;
};

const useChartComboData = (
  props: Props,
): {
  comboChartData: ChartData;
} => {
  const color = useColor();
  const { chart, tableData } = props;
  const getLabel = useChartLabel(tableData.field_item_meta);
  const getColor = useChartColor(chart.field);

  const singleTableData: ChartSingleTableRes = chartUtils.getSortedRowItems(
    chart,
    tableData as ChartSingleTableRes,
    chart.selected_field.items || [],
  ) as ChartSingleTableRes;

  const comboChartData: ChartData = useMemo(() => {
    if (!singleTableData.tables[0] || chart.series.length < 2) {
      return { labels: [], datasets: [] };
    }

    const primaryTable = singleTableData.tables[0];
    const secondaryTable = singleTableData.tables[1];

    const labels = primaryTable.map((item) =>
      getLabel(chart.field.type, item.x),
    );

    // Primary data (Bar chart) - 첫 번째 시리즈
    const primaryData = primaryTable.map((item) => item.value);

    // Secondary data (Line chart) - 두 번째 시리즈
    const secondaryData = secondaryTable
      ? secondaryTable.map((item) => item.value)
      : [];

    const datasets = [
      // Primary dataset (Bar) - lower z-index
      {
        type: 'bar' as const,
        data: primaryData,
        backgroundColor: primaryTable.map((item) =>
          getAlphaColor(getColor(item.x), 0.8),
        ),
        borderWidth: 1,
        yAxisID: 'y',
        order: 2, // Higher number = lower layer
        meta: {
          x: primaryTable.map((item) => item.x),
          y: primaryData,
        },
      },
      // Secondary dataset (Line) - higher z-index
      {
        type: 'line' as const,
        data: secondaryData,
        backgroundColor: getAlphaColor(color.gray700, 0.2),
        borderColor: color.gray700,
        borderWidth: 3, // Slightly thicker for better visibility
        fill: false,
        tension: 0,
        yAxisID: 'y1',
        order: 1, // Lower number = higher layer
        pointRadius: 4, // Make points more visible
        pointHoverRadius: 6,
        pointBackgroundColor: color.gray700,
        pointBorderColor: '#ffffff',
        pointBorderWidth: 2,
        meta: {
          x: secondaryTable?.map((item) => item.x) || [],
          y: secondaryData,
        },
      },
    ];

    // COMBO_CHART는 compare 기능을 지원하지 않음

    return {
      labels,
      datasets,
    };
  }, [
    chart.field.type,
    chart.series,
    color.gray700,
    getColor,
    getLabel,
    singleTableData.tables,
  ]);

  return { comboChartData };
};

export default useChartComboData;
